<script setup lang="ts">
import { mdiTriangleDown, mdiPencil, mdiShimmer, mdiWater, mdiHeart, mdiShareVariant, mdiHumanMaleBoy, mdiMusic, mdiPowerPlugOff, mdiPostageStamp, mdiAccountCircleOutline, mdiRobotOutline, mdiBookOpenPageVariantOutline, mdiFeather } from '@mdi/js'

defineProps<{
  compact?: boolean
}>()

const announceDialog = ref<boolean>(false)
const accountDialog = ref<boolean>(false)
const profileEditDialog = ref<boolean>(false)
// 冒険の手記の一覧（#900）。
const memoirDialog = ref<boolean>(false)
// スキルのカタログ（#933）。経験値 0 の型があるので、誰でも 1 つは持っている
const skillDialog = ref<boolean>(false)

const gameStateStore = useGameStateStore()
const { user: userInfo, memoirs } = storeToRefs(gameStateStore)
const { reloadGameState } = gameStateStore
const dialogStore = useDialogStore()

// 魔導士の名のり 編集ダイアログ (#417)。slug 設定済/未設定どちらも同じ
// UI で扱えるよう、ダイアログ内部で bio/links を fetch する設計に変更
// したので、呼び出し側は v-model を切り替えるだけ。
const onEditProfile = () => {
  profileEditDialog.value = true
}

const onProfileSaved = async () => {
  await reloadGameState()
}

// 冒険の手記（#900）。#971 から**全員に出す**: 額に「まだもらえる経験値」が載るので、1 冊も持っていない人
// （経験値 0 で羽根筆だけで遊ぶ人）にも、どこへ行けば経験値が増えるか（＝スキルが見えてくるか）を見せる
const hasMemoir = computed(() => (memoirs.value?.items?.length ?? 0) > 0)

// 「手記に何か増えた」ことを NEW 印で教える —— 獲得直後は本文を読んでいるので、これは
// per-item の未読印ではなく **手記という機能があること自体を教える装置**（1 本のフラグで
// 足りる）。一覧を開くと下りる。
const hasNewMemoir = computed(() => memoirs.value?.updated ?? false)

const onOpenMemoirs = () => {
  memoirDialog.value = true
}

const bgmEnabled = useMusicToggle()

const onChangeName = async (): Promise<void> => {
  if (userInfo.value) {
    // Show dialog and wait for it to close
    await dialogStore.showDialogAsync('name')

    // Reload game state after dialog is closed
    await reloadGameState()
  }
}

const onManageAccount = () => {
  accountDialog.value = true
}

// const onManageBilling = () => {
//   return navigateTo(
//     'https://billing.stripe.com/p/login/test_bIYbJc5048xh6A0aEE',
//     {
//       external: true,
//     },
//   )
// }

const onBack = (): void => {
  announceDialog.value = false
  accountDialog.value = false
}

const onStateChanged = async () => {
  await reloadGameState()
}

// Announcement

interface Announcement {
  title: string
  content: string
}

const announcements = ref<Array<Announcement>>([
  // {
  //     title: 'リリース記念70%OFF',
  //     content: `<p>Code & Magic リリースを記念して、70% OFF キャンペーンを開催中です。「妖精との仲介者」の依頼時に、下記のプロモーションコードを入力ください。</p>
  //         <div class="text-center my-4">
  //         <pre>ENCODIA2024</pre>
  //         </div>
  //         <p>2024年12月22日（日）までの期間限定です。この機会にぜひお申し込みください！</p>`
  // },
  {
    title: 'MFT2024来場者の方へ',
    content: `<p>Maker Faire Tokyo 2024 での本サービスのご体験およびご利用登録をいただき誠にありがとうございます。</p>
            <p>当日、魔導書をお持ち帰りいただいた方は、さらにアンロックキーの申し込みが必要です。</p>
            <p>大変お手数ですが、下記のURLよりお申し込みください。</p>
            <p><a href="https://forms.gle/J5vXKXj7w6UB7dP78" target="_black">申込フォーム</a></p>`,
  },
])

const announcementContent = ref<Announcement>({ title: '', content: '' })

const _onShowAnnouncement = (title: string) => {
  const announcement = announcements.value.find(item => item.title === title)
  if (announcement) {
    announcementContent.value = announcement
    announceDialog.value = true
  }
}
</script>

<template>
  <!-- userInfo は client-side のゲーム state から復元されるため SSR では
       null、CSR では loaded user という状態のズレが起きる。結果として
       v-btn の存在が SSR (コメントノード) と CSR (実 button) でぶつかり
       hydration node mismatch 警告が出る。ClientOnly で SSR から外す。 -->
  <ClientOnly>
    <v-btn
      v-if="userInfo"
      id="user-info-card"
      variant="text"
      class="user-info-card"
    >
      <!-- Resident panel -->
      <div
        v-if="!compact"
        class="user-card-inner"
      >
        <!-- 左：Avatar -->
        <div class="user-card-left">
          <v-avatar
            class="user-card-avatar"
            image="~/assets/images/user.png"
          />
        </div>

        <!-- 中央：名前 -->
        <div class="user-card-center">
          <div class="user-name">
            {{ userInfo.name }}
          </div>
        </div>

        <v-spacer />

        <!-- 右：メニューボタン -->
        <div class="user-card-right">
          <v-icon
            color="white"
            size="x-small"
          >
            {{ mdiTriangleDown }}
          </v-icon>
        </div>
      </div>
      <div
        v-else
        class="user-card-inner-compact"
      >
        <!-- 左：Avatar -->
        <div class="user-card-left">
          <v-avatar
            class="user-card-avatar"
            image="~/assets/images/user.png"
          />
        </div>

        <!-- 右：メニューボタン -->
        <div class="user-card-right">
          <v-icon
            color="white"
            size="x-small"
          >
            {{ mdiTriangleDown }}
          </v-icon>
        </div>
      </div>

      <!-- Full menu -->
      <v-menu
        activator="parent"
        :close-on-content-click="false"
        location="bottom end"
      >
        <v-sheet class="control-menu">
          <div class="menu-profile-section">
            <div class="user-card-left">
              <v-avatar
                class="menu-avatar"
                image="~/assets/images/user.png"
              />
            </div>

            <div class="user-card-center">
              <div class="user-name">
                {{ userInfo.name }}
              </div>
              <div class="user-rank">
                {{ userInfo.title }}
              </div>
            </div>

            <div class="user-card-right">
              <v-btn
                :icon="mdiPencil"
                variant="text"
                @click="onChangeName"
              />
            </div>
          </div>

          <v-divider />

          <v-list
            density="compact"
            class="menu-list-section"
          >
            <v-list-subheader>ステータス</v-list-subheader>
            <v-list-item
              density="compact"
              :prepend-icon="mdiShimmer"
            >
              <v-list-item-title>かくとくした経験値</v-list-item-title>
              <template #append>
                {{ userInfo?.exp || 0 }} ポイント
              </template>
            </v-list-item>
            <v-list-item
              density="compact"
              :prepend-icon="mdiWater"
            >
              <v-list-item-title>まどうエネルギー</v-list-item-title>
              <template #append>
                <!-- {{ userInfo.mpEnergy || 0 }} / {{ userInfo.mpCapacity || 0 }} -->
                <MagicPot
                  class="menu-magic-pot"
                  :energy="userInfo.mpEnergy || 0"
                  :capacity="userInfo.mpCapacity || 0"
                />
              </template>
            </v-list-item>
            <v-list-item
              density="compact"
              :prepend-icon="mdiPostageStamp"
            >
              <v-list-item-title>フクロウ便の切手</v-list-item-title>
              <template #append>
                {{ userInfo?.stamps || 0 }} 枚
              </template>
            </v-list-item>
            <v-list-item
              v-if="false"
              density="compact"
              :prepend-icon="mdiHeart"
            >
              <v-list-item-title>いいねの数 </v-list-item-title>
              <template #append>
                {{ userInfo?.likes || 0 }}
              </template>
            </v-list-item>
            <v-list-item
              v-if="false"
              density="compact"
              :prepend-icon="mdiShareVariant"
            >
              <v-list-item-title>デコードの数</v-list-item-title>
              <template #append>
                {{ userInfo?.exp || 0 }}
              </template>
            </v-list-item>

            <v-divider />

            <v-list-subheader>操作</v-list-subheader>
            <!-- 冒険の手記（#900）。イベントダイアログ（DialogEventDialog）を持つ画面から
                 しか開けないが、UserInfoCard を置いているのは SceneMap と Playground
                 だけで、どちらもそれを持っている。 -->
            <v-list-item
              v-if="hasMemoir"
              density="compact"
              :prepend-icon="mdiBookOpenPageVariantOutline"
              title="冒険の手記"
              data-testid="menu-memoirs"
              @click="onOpenMemoirs"
            >
              <template #append>
                <v-chip
                  v-if="hasNewMemoir"
                  size="x-small"
                  color="error"
                  variant="flat"
                  data-testid="memoir-new-badge"
                >
                  NEW
                </v-chip>
              </template>
            </v-list-item>
            <v-list-item
              density="compact"
              :prepend-icon="mdiFeather"
              title="スキル"
              data-testid="menu-skills"
              @click="skillDialog = true"
            />
            <!-- <v-list-item title="名前を変える" @click="onChangeName" /> -->
            <!-- <v-list-item v-if="false" title="支払の管理" @click="onManageBilling" /> -->
            <v-list-item
              density="compact"
              :prepend-icon="mdiAccountCircleOutline"
              :title="userInfo.slug ? '魔導士の名のりを編集' : '魔導士の名のりを作成'"
              @click="onEditProfile"
            />
            <v-list-item
              v-if="!userInfo.isChild"
              density="compact"
              :prepend-icon="mdiHumanMaleBoy"
              title="子アカウント管理"
              data-testid="menu-children"
              @click="onManageAccount"
            />
            <v-list-item
              v-if="!userInfo.isChild"
              density="compact"
              :prepend-icon="mdiRobotOutline"
              title="外部エージェント接続"
              @click="navigateTo('/settings/agent-connections')"
            />
            <v-list-item
              v-if="false"
              density="compact"
              title="お知らせ"
            />
            <v-list-item
              :prepend-icon="mdiMusic"
              title="BGMの再生"
            >
              <template #append>
                <v-switch
                  v-model="bgmEnabled"
                  density="compact"
                  color="primary"
                  inset
                  hide-details
                />
              </template>
            </v-list-item>

            <v-divider />

            <v-list-item
              density="compact"
              :prepend-icon="mdiPowerPlugOff"
              title="サインアウト"
              @click="navigateTo('/signout')"
            />
          </v-list>
        </v-sheet>

        <!-- Child Account Dialog -->
        <dialog-ListChildrenDialog
          v-model="accountDialog"
          @state-changed="onStateChanged"
        />

        <!-- 冒険の手記の一覧 (#900) -->
        <DialogMemoirListDialog v-model="memoirDialog" />
        <DialogSkillListDialog v-model="skillDialog" />

        <!-- 魔導士の名のり 編集ダイアログ (#417) -->
        <DialogMagicianProfileEditDialog
          v-model="profileEditDialog"
          @saved="onProfileSaved"
        />

        <!-- Notification Dialog -->
        <Dialog
          v-model="announceDialog"
          :title="announcementContent.title"
          :cancel-button="{ title: 'とじる' }"
          @cancel="onBack"
        >
          <template #default>
            <!-- eslint-disable-next-line vue/no-v-html -- announcement content is from trusted server data -->
            <div v-html="announcementContent.content" />
          </template>
        </Dialog>
      </v-menu>
    </v-btn>
  </ClientOnly>
</template>

<style scoped>
.user-info-card {
  font-family: "Noto Sans JP", sans-serif;
  /* width: clamp(171.43px, 22.5dvw, 360px); */
  height: max(33.33px, calc(3.75 * var(--u)));
  background-color: transparent;
  border-radius: max(11.11px, calc(1.563 * var(--u)));

  padding: 0 max(7.62px, calc(1 * var(--u)));

  z-index: 999;
}

.user-card-inner {
  display: flex;
  align-items: center;
  justify-items: stretch;

  /* width: clamp(171.43px, 22.5dvw, 360px); */
  width: max(157.14px, calc(20.625 * var(--u)));
  /* width: 100%; */
  height: 100%;

  padding: 0;

  gap: max(3.81px, calc(0.5 * var(--u)));

  font-size: max(11.11px, calc(1.563 * var(--u)));
  text-transform: none;
}

.user-card-inner-compact {
  display: flex;
  align-items: center;
  justify-items: stretch;

  /* width: clamp(171.43px, 22.5dvw, 360px); */
  width: max(33.33px, calc(3.75 * var(--u)));
  /* width: 100%; */
  height: 100%;

  padding: 0;

  /* gap: clamp(3.81px, 0.5dvw, 8px); */
  gap: max(1.91px, calc(0.25 * var(--u)));

  font-size: max(11.11px, calc(1.563 * var(--u)));
  text-transform: none;
}

.user-card-left {
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-start;
}

.user-card-center {
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.user-card-right {
  font-size: 80%;
  color: #606060;
  flex: 0 0 auto;
  align-content: center;
  justify-content: flex-end;
}

.user-card-right :deep(.v-btn) {
  /* --v-btn-height: max(11.43px, calc(1.5 * var(--u))); */
  width: max(22.86px, calc(3 * var(--u)));
  height: max(22.86px, calc(3 * var(--u)));
}

.user-card-right :deep(.v-icon) {
  font-size: max(11.43px, calc(1.5 * var(--u)));
  width: max(11.43px, calc(1.5 * var(--u)));
  height: max(11.43px, calc(1.5 * var(--u)));
  min-width: auto;
  min-height: auto;
  line-height: normal;
}

.user-card-avatar {
  width: max(19.05px, calc(2.5 * var(--u)));
  height: max(19.05px, calc(2.5 * var(--u)));
}

.user-name {
  font-weight: bold;

  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow:
    2px 0 white,
    -2px 0 white,
    0 2px white,
    0 -2px white,
    2px 2px white,
    -2px -2px white,
    2px -2px white,
    -2px 2px white;
}

/* #719: モバイル（狭幅）では暗いヘッダースクリム上に出るので、白 8 方向の縁取り
   （--u 前提で小サイズだとがたつく）をやめ、白文字＋やわらかい影にする。
   .user-card-inner 配下の常設パネル名だけが対象で、ライト背景のメニュー内
   （.menu-profile-section の .user-name）には効かせない。デスクトップの
   マップ直上（>=769px）は従来の縁取りを維持。 */
@media (max-width: 768px) {
  .user-card-inner .user-name {
    color: #fff;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.85);
  }

  /* #721: モバイルヘッダーは compact 表示（名前は出さない）。狭幅で潰れた名前より
     視認できるアバターの方が情報になるので、アバターを拡大する。ボタン全体の
     高さも合わせて広げ、▽アイコンも少し大きくして押しやすくする。
     ※ 当初 44px にしたが大きすぎたので 80%（≈35px）に、▽も比率合わせで縮小（#721 2巡目）。 */
  .user-info-card {
    height: auto;
  }

  .user-card-inner-compact {
    width: auto;
    gap: 4px;
  }

  .user-card-inner-compact .user-card-avatar {
    width: 35px;
    height: 35px;
  }

  .user-card-inner-compact .user-card-right :deep(.v-icon) {
    font-size: 14px;
    width: 14px;
    height: 14px;
  }
}

.user-rank {
  color: #606060;

  font-size: 70%;
  font-weight: 700;

  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.control-menu {
  width: max(228.57px, calc(30 * var(--u)));

  font-family: "Noto Sans JP", sans-serif;
  font-size: max(11px, calc(1.3 * var(--u)));
  font-weight: 700;
}

.menu-profile-section {
  background-color: aliceblue;

  display: flex;
  justify-items: stretch;

  width: 100%;
  height: 100%;

  padding: max(3.81px, calc(0.5 * var(--u)));

  gap: max(3.81px, calc(0.5 * var(--u)));

  font-size: 150%;
}

.menu-avatar {
  width: max(38px, calc(5 * var(--u)));
  height: max(38px, calc(5 * var(--u)));
  margin: 0 max(1.91px, calc(0.25 * var(--u)));
}

.menu-list-section {
  background-color: aliceblue;

  padding: 0;
}

.menu-list-section :deep(.v-list-item) {
  padding: 0 max(7.62px, calc(1 * var(--u)));

  --list-item-height: max(33.33px, calc(3.75 * var(--u)));
  /* --list-item-height: max(19.05px, calc(2.5 * var(--u))); */
  height: var(--list-item-height);
  min-height: var(--list-item-height);
}

.menu-list-section :deep(.v-list-item-title, .v-list-subheader, .v-icon--size-default) {
  font-size: inherit;
  font-weight: inherit;

  /* min-height: auto; */
}

.menu-list-section :deep(.v-label) {
  font-size: max(11px, calc(1.3 * var(--u)));

  padding: 0 max(7.62px, calc(1 * var(--u)));
}

.menu-magic-pot {
  position: absolute;
  transform: translate(-100%, -5%);
}
</style>
